import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Gavel, FileSignature, Copyright, Building2, ShieldCheck, TrendingUp, HelpCircle, Lock,
} from "lucide-react";
import { submitConsultation, formError } from "@/lib/api";
import { SERVICES, BRAND } from "@/lib/content";
import useSEO from "@/lib/useSEO";

const ICONS = { Gavel, FileSignature, Copyright, Building2, ShieldCheck, TrendingUp };
const URGENCY = ["Today / within 24 hours", "This week", "This month", "Planning ahead"];
const PLATFORMS = ["YouTube", "TikTok", "Instagram", "Twitch", "X", "Substack", "Patreon", "Podcast", "Kick", "Other"];
const AUDIENCE = ["Under 10K", "10K to 100K", "100K to 1M", "Over 1M", "Not audience-based"];
const CREATOR_TYPES = ["Creator / influencer", "Streamer", "Podcaster", "Newsletter writer", "Online educator", "Agency or manager", "Brand or business", "Other"];
const BUDGET = ["Not sure yet", "Under $1,500", "$1,500 to $5,000", "$5,000 to $15,000", "$15,000+"];
const STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "District of Columbia", "Florida",
  "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts",
  "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico",
  "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina",
  "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming",
  "Outside the United States",
];

const initial = {
  service_interest: "", urgency: "", message: "", deadline: "", platforms: [], audience_size: "",
  name: "", email: "", phone: "", location: "", creator_type: "", budget_range: "",
  consent: false, referral_ack: false, website: "",
};

const STEPS = ["The issue", "Details", "About you", "Send"];

export default function Start() {
  useSEO({
    title: "Start a request | Upload Counsel",
    description: "Tell us what's going on. A licensed attorney reviews every request and matches you with vetted counsel for brand deals, platform disputes, trademarks, business setup and more.",
    canonical: "https://uploadlawyer.com/start",
  });
  const [form, setForm] = useState(initial);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k, e.target.type === "checkbox" ? e.target.checked : e.target.value);
  const togglePlatform = (p) =>
    set("platforms", form.platforms.includes(p) ? form.platforms.filter((x) => x !== p) : [...form.platforms, p]);

  const problem = () => {
    if (step === 0 && !form.service_interest) return "Pick the closest match, or \"Something else\".";
    if (step === 1 && form.message.trim().length < 20) return "Describe what happened in a sentence or two (20+ characters).";
    if (step === 2) {
      if (!form.name.trim()) return "Add your name.";
      if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return "Add an email we can reach you at.";
      if (!form.location) return "Pick where you're based, so we match an attorney licensed there.";
    }
    if (step === 3 && (!form.consent || !form.referral_ack)) return "Please confirm both statements to send.";
    return null;
  };

  const next = () => {
    const p = problem();
    if (p) { toast.error(p); return; }
    setStep((s) => s + 1);
    const f = document.querySelector('[data-testid="consultation-form"]');
    if (f) window.scrollTo({ top: f.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" });
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (step < 3) { next(); return; }
    const p = problem();
    if (p) { toast.error(p); return; }
    setLoading(true);
    try {
      await submitConsultation({ ...form, platforms: form.platforms.join(", ") });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formError(err, BRAND.email));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="page-contact" className="max-w-3xl mx-auto px-5 lg:px-8 py-20 md:py-28">
        <div data-testid="consultation-success" className="rounded-3xl border border-[#D9B36C]/40 bg-[#121419] p-8 sm:p-12">
          <CheckCircle2 size={36} className="text-[#D9B36C]" />
          <h1 className="mt-5 font-display text-4xl sm:text-5xl text-[#F4F1EA]">Request received.</h1>
          <p className="mt-4 text-slate-300 leading-relaxed">
            A licensed attorney will review it and aims to reply to <span className="text-[#F4F1EA]">{form.email}</span> within one business day.
            Urgent platform matters are looked at first.
          </p>
          <p className="mt-3 text-slate-500 text-sm">
            Sending a request does not create an attorney-client relationship. That begins only with a signed engagement letter.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/blog" className="inline-flex items-center gap-2 bg-[#D9B36C] text-[#0B0C10] font-semibold px-6 py-3 rounded-full">Read Insights</Link>
            <Link to="/" className="inline-flex items-center gap-2 border border-[#272B36] text-[#F4F1EA] font-medium px-6 py-3 rounded-full hover:bg-white/5">Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-contact">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 glow-tl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-8 md:pt-20">
          <p className="eyebrow">Start a request</p>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl text-[#F4F1EA] max-w-3xl leading-[1.02]">Tell us what's going on.</h1>
          <p className="mt-5 text-slate-300 text-lg max-w-2xl leading-relaxed">
            About five minutes. Free. A licensed attorney reads every request, and we aim to reply within one business day.
          </p>
        </div>
      </section>

      <section className="relative pb-16 pt-4">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          <form onSubmit={submit} data-testid="consultation-form" className="lg:col-span-8" noValidate>
            {/* progress */}
            <ol className="flex gap-2 mb-6" aria-label="Progress">
              {STEPS.map((s, i) => (
                <li key={s} className="flex-1">
                  <div className={`h-1 rounded-full ${i <= step ? "bg-[#D9B36C]" : "bg-[#272B36]"}`} />
                  <p className={`mt-2 text-xs ${i === step ? "text-[#F4F1EA]" : "text-slate-500"}`}>
                    <span className="hidden sm:inline">{i + 1}. </span>{s}
                  </p>
                </li>
              ))}
            </ol>

            <div className="rounded-3xl border border-[#272B36] bg-[#121419] p-6 sm:p-8 md:p-10">
              {/* honeypot: hidden from people, filled by bots */}
              <input type="text" name="website" value={form.website} onChange={onInput("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

              {step === 0 && (
                <div className="space-y-8">
                  <div>
                    <h2 className="font-display text-3xl text-[#F4F1EA]">What is it about?</h2>
                    <p className="mt-1 text-sm text-slate-400">Pick the closest match. You can explain the rest next.</p>
                    <div className="mt-5 grid sm:grid-cols-2 gap-3" role="radiogroup">
                      {[...SERVICES, { slug: "other", title: "Something else", short: "Not sure, or it doesn't fit these.", icon: "Help" }].map((s) => {
                        const Icon = ICONS[s.icon] || HelpCircle;
                        const val = s.slug === "other" ? "Not sure" : s.title;
                        const on = form.service_interest === val;
                        return (
                          <button
                            type="button"
                            role="radio"
                            aria-checked={on}
                            key={s.slug}
                            data-testid={`intake-area-${s.slug}`}
                            onClick={() => set("service_interest", val)}
                            className={`text-left rounded-2xl border p-4 transition-colors flex gap-3 ${on ? "border-[#D9B36C] bg-[#D9B36C]/10" : "border-[#272B36] bg-[#0F1116] hover:border-[#D9B36C]/40"}`}
                          >
                            <Icon size={18} className={`mt-0.5 shrink-0 ${on ? "text-[#D9B36C]" : "text-slate-400"}`} />
                            <span>
                              <span className="block text-sm font-semibold text-[#F4F1EA]">{s.title}</span>
                              <span className="block text-xs text-slate-400 mt-1 leading-snug">{s.short}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#F4F1EA]">How soon does it need attention?</p>
                    <Chips options={URGENCY} value={form.urgency} onPick={(v) => set("urgency", v)} testid="contact-urgency" />
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-6">
                  <h2 className="font-display text-3xl text-[#F4F1EA]">What happened?</h2>
                  <Field label="Describe the situation" required hint="What happened, who's involved, what you've already tried. Leave out anything highly sensitive for now.">
                    <textarea
                      data-testid="contact-message"
                      rows={7}
                      value={form.message}
                      maxLength={8000}
                      onChange={onInput("message")}
                      className={`${inputCls} resize-y`}
                      placeholder="e.g. A brand sent a sponsorship contract with a 12-month exclusivity clause. They want it signed by Friday."
                    />
                  </Field>
                  <Field label="Any deadline?" hint="A response date, a hearing, a launch. Optional.">
                    <input data-testid="contact-deadline" value={form.deadline} onChange={onInput("deadline")} maxLength={200} className={inputCls} placeholder="e.g. Friday, Nov 14" />
                  </Field>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Platforms involved</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {PLATFORMS.map((p) => {
                        const on = form.platforms.includes(p);
                        return (
                          <button
                            type="button"
                            key={p}
                            aria-pressed={on}
                            onClick={() => togglePlatform(p)}
                            className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${on ? "border-[#D9B36C] bg-[#D9B36C]/15 text-[#F4F1EA]" : "border-[#272B36] text-slate-300 hover:border-[#D9B36C]/40"}`}
                          >
                            {p}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <Field label="Audience size">
                    <select data-testid="contact-audience" value={form.audience_size} onChange={onInput("audience_size")} className={inputCls}>
                      <option value="">Select…</option>
                      {AUDIENCE.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </Field>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <h2 className="font-display text-3xl text-[#F4F1EA]">Who should we reply to?</h2>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Your name" required>
                      <input data-testid="contact-name" value={form.name} onChange={onInput("name")} maxLength={200} className={inputCls} autoComplete="name" />
                    </Field>
                    <Field label="Email" required>
                      <input data-testid="contact-email" type="email" value={form.email} onChange={onInput("email")} className={inputCls} autoComplete="email" />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Where you're based" required hint="So we match an attorney licensed there.">
                      <select data-testid="contact-location" value={form.location} onChange={onInput("location")} className={inputCls}>
                        <option value="">Select…</option>
                        {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    <Field label="Phone" hint="Optional. Only for urgent matters.">
                      <input data-testid="contact-phone" type="tel" value={form.phone} onChange={onInput("phone")} maxLength={40} className={inputCls} autoComplete="tel" />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="You are a…">
                      <select data-testid="contact-creator-type" value={form.creator_type} onChange={onInput("creator_type")} className={inputCls}>
                        <option value="">Select…</option>
                        {CREATOR_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </Field>
                    <Field label="Budget in mind" hint="Optional. Helps us match the right fee structure.">
                      <select data-testid="contact-budget" value={form.budget_range} onChange={onInput("budget_range")} className={inputCls}>
                        <option value="">Select…</option>
                        {BUDGET.map((b) => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </Field>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6">
                  <h2 className="font-display text-3xl text-[#F4F1EA]">Check and send.</h2>
                  <dl className="rounded-2xl border border-[#272B36] bg-[#0F1116] divide-y divide-[#272B36] text-sm">
                    {[
                      ["About", form.service_interest],
                      ["Urgency", form.urgency || "Not given"],
                      ["Platforms", form.platforms.join(", ") || "Not given"],
                      ["Based in", form.location],
                      ["Reply to", `${form.name} · ${form.email}`],
                    ].map(([k, v]) => (
                      <div key={k} className="flex gap-4 px-5 py-3">
                        <dt className="w-24 shrink-0 text-slate-500">{k}</dt>
                        <dd className="text-slate-200 break-words min-w-0">{v}</dd>
                      </div>
                    ))}
                    <div className="px-5 py-3">
                      <dt className="text-slate-500">What happened</dt>
                      <dd className="mt-1 text-slate-300 whitespace-pre-line line-clamp-4">{form.message}</dd>
                    </div>
                  </dl>

                  <Check
                    testid="contact-consent"
                    checked={form.consent}
                    onChange={onInput("consent")}
                  >
                    I understand Upload Counsel is a legal concierge and referral service, not a law firm; that sending this request does not
                    create an attorney-client relationship; and that I would engage any attorney under their own written engagement letter. I have read the{" "}
                    <Link to="/legal/disclaimer" className="text-[#D9B36C] underline" target="_blank">disclaimer</Link>.
                  </Check>
                  <Check
                    testid="contact-referral-ack"
                    checked={form.referral_ack}
                    onChange={onInput("referral_ack")}
                  >
                    <span className="text-slate-200 font-medium">Referral.</span> Upload Counsel may share this request with an attorney in its
                    network, or the founder's affiliated firm, to match my matter. Any division of fees between attorneys will follow the
                    applicable rules of professional conduct and be disclosed to me in writing for my consent before I engage anyone.
                  </Check>
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-[#272B36] flex items-center justify-between gap-3">
                {step > 0 ? (
                  <button type="button" onClick={back} className="inline-flex items-center gap-2 text-slate-300 hover:text-[#F4F1EA] px-2 py-2">
                    <ArrowLeft size={16} /> Back
                  </button>
                ) : <span />}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid={step === 3 ? "contact-submit" : "intake-next"}
                  className="inline-flex items-center justify-center gap-2 bg-[#D9B36C] text-[#0B0C10] font-semibold px-7 py-3.5 rounded-full hover:bg-[#E5C688] transition-colors disabled:opacity-50"
                >
                  {step === 3 ? (loading ? "Sending…" : "Send request") : <>Continue <ArrowRight size={16} /></>}
                </button>
              </div>
            </div>
          </form>

          <aside className="lg:col-span-4 space-y-4 lg:pt-12">
            <div className="rounded-2xl border border-[#272B36] bg-[#121419] p-6">
              <p className="eyebrow">What happens next</p>
              <ol className="mt-4 space-y-3 text-sm text-slate-300">
                <li className="flex gap-3"><span className="font-display text-[#D9B36C] text-lg leading-none w-5">1</span> A licensed attorney reviews your request, usually within one business day.</li>
                <li className="flex gap-3"><span className="font-display text-[#D9B36C] text-lg leading-none w-5">2</span> We reply with an honest read, including when you don't need a lawyer.</li>
                <li className="flex gap-3"><span className="font-display text-[#D9B36C] text-lg leading-none w-5">3</span> If you do, we introduce you to a vetted attorney licensed where you need one.</li>
                <li className="flex gap-3"><span className="font-display text-[#D9B36C] text-lg leading-none w-5">4</span> They send a written scope and price. You decide.</li>
              </ol>
            </div>
            <div className="rounded-2xl border border-[#272B36] bg-[#121419] p-6">
              <p className="eyebrow flex items-center gap-2"><Lock size={12} /> Confidential</p>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                Your request is used only to review and match your matter. Privilege fully attaches once you engage a specific attorney.
              </p>
            </div>
            <p className="text-sm text-slate-500 px-1">
              Prefer email? <a href={`mailto:${BRAND.email}`} className="text-[#D9B36C] underline underline-offset-2">{BRAND.email}</a>
            </p>
          </aside>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-[#0F1116] border border-[#272B36] rounded-xl px-4 py-3 text-[#F4F1EA] text-base sm:text-sm placeholder:text-slate-600 outline-none focus:border-[#D9B36C] focus:ring-1 focus:ring-[#D9B36C] transition-colors";

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-slate-400 font-semibold">
        {label} {required && <span className="text-[#D9B36C]">*</span>}
      </span>
      <div className="mt-2">{children}</div>
      {hint && <span className="block mt-1.5 text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

function Chips({ options, value, onPick, testid }) {
  return (
    <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" data-testid={testid}>
      {options.map((o) => (
        <button
          type="button"
          role="radio"
          aria-checked={value === o}
          key={o}
          onClick={() => onPick(value === o ? "" : o)}
          className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${value === o ? "border-[#D9B36C] bg-[#D9B36C]/15 text-[#F4F1EA]" : "border-[#272B36] text-slate-300 hover:border-[#D9B36C]/40"}`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function Check({ testid, checked, onChange, children }) {
  return (
    <label className="flex items-start gap-3 text-sm text-slate-400 cursor-pointer leading-relaxed">
      <input type="checkbox" data-testid={testid} checked={checked} onChange={onChange} className="mt-1 h-4 w-4 shrink-0 accent-[#D9B36C]" />
      <span>{children}</span>
    </label>
  );
}
